import React, { useRef, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Upload, Link as LinkIcon, Loader2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

/**
 * Hybrid image input: paste a URL OR upload a file.
 * Calls onChange(url) with the resulting URL string.
 */
export default function ImageInput({ value = "", onChange, placeholder = "https://... ou clique em Enviar", aspect = "16/9", testid = "img-input", showPreview = true }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // backend returns relative /api/uploads/{name} → prepend backend origin so <img src> resolves cross-origin
      const url = (process.env.REACT_APP_BACKEND_URL || "") + data.url;
      onChange?.(url);
      toast.success("Imagem enviada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <LinkIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none"/>
          <Input
            value={value || ""}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder}
            className="rounded-xl pl-8"
            data-testid={`${testid}-url`}
          />
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" hidden onChange={handleFile} data-testid={`${testid}-file`}/>
        <Button type="button" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} className="rounded-xl shrink-0" data-testid={`${testid}-upload-btn`}>
          {busy ? <Loader2 size={14} className="animate-spin mr-1.5"/> : <Upload size={14} className="mr-1.5"/>}
          Enviar
        </Button>
        {value && (
          <Button type="button" variant="ghost" onClick={() => onChange?.("")} className="rounded-xl shrink-0 text-stone-500" data-testid={`${testid}-clear-btn`} title="Remover">
            <X size={14}/>
          </Button>
        )}
      </div>
      {showPreview && value && (
        <div className="rounded-xl overflow-hidden bg-stone-100 border border-stone-200" style={{ aspectRatio: aspect, maxWidth: 360 }}>
          <img src={value} alt="preview" className="w-full h-full object-cover"/>
        </div>
      )}
    </div>
  );
}
